import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, ArrowRight, Bell, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { US_STATES, CATEGORIES } from "@/lib/constants";
import AdBanner from "@/components/AdBanner";
import SEOHead, { generateWebPageSchema, generateBreadcrumbSchema } from "@/components/SEOHead";
import { getImageUrl } from "@/lib/imageUtils";
import api from "@/lib/api";

export default function StatePage() {
  const { locationSlug, stateSlug } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [waitlistStatus, setWaitlistStatus] = useState(null);
  const [waitlistCount, setWaitlistCount] = useState(0);

  // Use either stateSlug (direct route) or locationSlug (via LocationPage)
  const actualStateSlug = stateSlug || locationSlug;
  const state = US_STATES[actualStateSlug];
  const isComingSoon = state?.comingSoon === true;

  useEffect(() => {
    if (!state) return;
    
    // For coming soon states, get waitlist count
    if (isComingSoon) {
      api.get(`/waitlist/count/${encodeURIComponent(state.name)}`)
        .then(res => setWaitlistCount(res.data.count))
        .catch(console.error)
        .finally(() => setLoading(false));
      return;
    }
    
    // Fetch items from all cities in this state
    const cityNames = state.cities.map(c => `${c.name}, ${state.abbrev}`);
    
    api.get("/items", { params: { limit: 50 } })
      .then((res) => {
        const stateItems = res.data.items.filter(item => 
          cityNames.some(cityName => item.location?.includes(cityName)) ||
          item.location?.includes(state.name) ||
          item.location?.includes(`, ${state.abbrev}`)
        );
        setItems(stateItems);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [actualStateSlug, state, isComingSoon]);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    try {
      const res = await api.post("/waitlist", { email, location: state.name });
      setWaitlistStatus({ type: "success", message: res.data.message });
      setEmail("");
      setWaitlistCount(prev => prev + 1);
    } catch (err) {
      setWaitlistStatus({ type: "error", message: err.response?.data?.detail || "Something went wrong" });
    }
  };

  if (!state) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <h1 className="font-display text-2xl text-ink mb-2">State Not Found</h1>
        <p className="text-ink-light mb-6">We couldn't find this state.</p>
        <Link to="/"><Button className="rounded-full bg-coral hover:bg-[#d44814] text-white">Back to Home</Button></Link>
      </div>
    );
  }

  // SEO Data
  const pageTitle = isComingSoon 
    ? `Paradise Borrowed Coming Soon to ${state.name} | Join the Waitlist`
    : `Borrow Items in ${state.name} | Paradise Borrowed™`;
  const pageDescription = isComingSoon
    ? `Paradise Borrowed is coming to ${state.name}! Be the first to know when you can borrow and lend items from neighbors in ${state.cities.slice(0, 3).map(c => c.name).join(", ")}, and more.`
    : `Find items to borrow from neighbors across ${state.name}. Baby gear, tools, electronics, outdoor equipment and more. Save money with Paradise Borrowed.`;
  const canonicalUrl = `https://www.paradiseborrowed.com/${actualStateSlug}`;

  // Schema.org structured data
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      generateWebPageSchema({
        title: pageTitle,
        description: pageDescription,
        url: canonicalUrl
      }),
      generateBreadcrumbSchema([
        { name: "Home", url: "https://www.paradiseborrowed.com" },
        { name: state.name, url: canonicalUrl }
      ]),
      {
        "@type": "State",
        "name": state.name,
        "containedInPlace": {
          "@type": "Country",
          "name": "United States"
        }
      }
    ]
  };

  // Coming Soon Page
  if (isComingSoon) {
    return (
      <div data-testid="state-page-coming-soon">
        <SEOHead
          title={pageTitle}
          description={pageDescription}
          canonicalUrl={canonicalUrl}
          schema={schema}
        />

        {/* Hero - Coming Soon */}
        <section className="bg-sand py-20 sm:py-28">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center gap-2 text-sm text-ink-light mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-coral transition-colors">Home</Link>
              <span>/</span>
              <span className="text-ink">{state.name}</span>
            </nav>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-coral-light text-coral text-sm font-medium mb-6">
              <Bell className="w-4 h-4" />
              Coming Soon
            </div>
            
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ink leading-tight tracking-tight mb-6" data-testid="state-headline">
              Paradise Borrowed is<br />coming to {state.name}
            </h1>
            
            <p className="text-lg text-ink-light max-w-2xl mx-auto mb-10 font-ui leading-relaxed">
              We're working hard to bring the borrowers economy to {state.name}. Be the first to know when you can start borrowing and lending items from neighbors in your area.
            </p>

            {/* Email Signup */}
            <form onSubmit={handleWaitlistSubmit} className="max-w-md mx-auto">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-light" />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 rounded-full border border-ink/10 bg-white text-ink focus:outline-none focus:ring-2 focus:ring-coral-light focus:border-coral font-ui"
                    required
                  />
                </div>
                <Button type="submit" className="rounded-full px-6 bg-coral hover:bg-[#d44814] text-white font-ui">
                  Notify Me
                </Button>
              </div>
              {waitlistStatus && (
                <p className={`mt-3 text-sm ${waitlistStatus.type === "success" ? "text-teal" : "text-red-500"}`}>
                  {waitlistStatus.message}
                </p>
              )}
              {waitlistCount > 0 && (
                <p className="mt-3 text-sm text-ink-light">
                  {waitlistCount} {waitlistCount === 1 ? "person is" : "people are"} already on the waitlist
                </p>
              )}
            </form>
          </div>
        </section>

        {/* Cities Preview */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl sm:text-3xl text-ink mb-8 text-center tracking-tight">
              {actualStateSlug === 'washington-dc' ? 'Neighborhoods' : 'Cities'} We'll Serve in {state.name}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {state.cities.map((city) => (
                <div
                  key={city.slug}
                  className="p-4 rounded-xl border border-ink/10 bg-sand/50"
                >
                  <span className="font-medium text-ink-light">
                    {city.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories Preview */}
        <section className="py-16 bg-sand">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl sm:text-3xl text-ink mb-8 text-center tracking-tight">
              What You'll Be Able to Borrow
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {CATEGORIES.slice(0, 12).map((cat) => {
                const Icon = cat.icon;
                return (
                  <div
                    key={cat.name}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl border border-ink/10 bg-white"
                  >
                    <Icon className="w-6 h-6 text-coral" />
                    <span className="text-xs text-center text-ink-light font-ui">{cat.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SEO Content */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-2xl sm:text-3xl text-ink mb-6 tracking-tight">
              About Paradise Borrowed in {state.name}
            </h2>
            <div className="prose prose-stone max-w-none font-ui">
              <p className="text-ink-light leading-relaxed mb-4">
                Paradise Borrowed is the peer-to-peer borrowing platform that connects neighbors who want to borrow items with those who have them to lend. We're excited to announce that we'll soon be launching in {state.name}, bringing our community-driven marketplace to {actualStateSlug === 'washington-dc' ? 'neighborhoods' : 'cities'} like {state.cities.map(c => c.name).join(", ")}.
              </p>
              <p className="text-ink-light leading-relaxed mb-4">
                Whether you need baby gear for visiting grandchildren, power tools for a weekend project, camping equipment for an adventure, or photography gear for a special event — Paradise Borrowed makes it easy to find what you need from trusted neighbors at a fraction of the retail price.
              </p>
              <p className="text-ink-light leading-relaxed">
                Join our waitlist today to be the first to know when Paradise Borrowed launches in {state.name}. Early members get exclusive benefits and the opportunity to be founding lenders in their community.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-teal">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-4 tracking-tight">
              Don't Miss the Launch
            </h2>
            <p className="text-white/80 max-w-md mx-auto mb-8 font-ui">
              Sign up now and be the first to borrow and lend in {state.name}.
            </p>
            <form onSubmit={handleWaitlistSubmit} className="max-w-md mx-auto">
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-4 py-3 rounded-full border-0 bg-white/10 text-white placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-white/30 font-ui"
                  required
                />
                <Button type="submit" className="rounded-full px-6 bg-white hover:bg-sand text-teal font-ui">
                  Join Waitlist
                </Button>
              </div>
            </form>
          </div>
        </section>
      </div>
    );
  }

  // Active State Page (existing functionality)
  return (
    <div data-testid="state-page">
      {/* SEO Head */}
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        schema={schema}
      />

      {/* Hero */}
      <section className="bg-sand py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-ink-light mb-4" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-coral transition-colors">Home</Link>
            <span>/</span>
            <span className="text-ink">{state.name}</span>
          </nav>
          
          <h1 className="font-display text-4xl sm:text-5xl text-ink leading-tight tracking-tight" data-testid="state-headline">
            Borrow Items in {state.name}
          </h1>
          <p className="mt-4 text-base text-ink-light leading-relaxed max-w-2xl font-ui">
            Find items to borrow from neighbors across {state.name}. From baby gear and tools to electronics and outdoor equipment — save money by borrowing local.
          </p>
        </div>
      </section>

      {/* Ad Banner - Top Leaderboard */}
      <div className="py-4 bg-white border-b border-ink/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AdBanner size="responsive" position="top-leaderboard" />
        </div>
      </div>

      {/* Cities Grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-xl sm:text-2xl text-ink mb-8 tracking-tight">
            Browse {actualStateSlug === 'washington-dc' ? 'Neighborhoods' : 'Cities'} in {state.name}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {state.cities.map((city) => (
              <Link
                key={city.slug}
                to={`/${actualStateSlug}/${city.slug}`}
                className="group p-4 rounded-xl border border-ink/10 bg-white hover:border-coral hover:shadow-md transition-all"
                data-testid={`city-link-${city.slug}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-ink group-hover:text-coral transition-colors font-ui">
                    {city.name}
                  </span>
                  <ArrowRight className="w-4 h-4 text-ink-light group-hover:text-coral group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Ad Banner - Mid Content */}
      <div className="py-6 bg-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AdBanner size="banner" position="mid-content" />
        </div>
      </div>

      {/* Categories */}
      <section className="py-12 bg-white border-y border-ink/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-xl sm:text-2xl text-ink mb-6 tracking-tight">
            Popular Categories in {state.name}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {CATEGORIES.slice(0, 12).map((cat) => {
              const Icon = cat.icon;
              return (
                <Link
                  key={cat.name}
                  to={`/browse?category=${encodeURIComponent(cat.name)}`}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border border-ink/10 bg-white hover:border-coral hover:shadow-sm transition-all"
                >
                  <Icon className="w-6 h-6 text-coral" />
                  <span className="text-xs text-center text-ink-light font-ui">{cat.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Items in State */}
      {items.length > 0 && (
        <section className="py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <h2 className="font-display text-xl sm:text-2xl text-ink tracking-tight">
                Recently Listed in {state.name}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {items.slice(0, 8).map((item) => (
                <Link
                  key={item.id}
                  to={`/items/${item.id}`}
                  className="rounded-2xl border border-ink/10 bg-white overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-[4/3] bg-sand-dark">
                    <img src={getImageUrl(item.images?.[0])} alt={item.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-ink-light mb-1 font-ui">{item.location}</p>
                    <h3 className="font-medium text-ink line-clamp-1 font-ui">{item.title}</h3>
                    <p className="text-coral font-semibold mt-1 font-ui">${item.price_per_day}/day</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SEO Content */}
      <section className="py-12 sm:py-16 bg-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="font-display text-xl sm:text-2xl text-ink mb-4 tracking-tight">
              About Paradise Borrowed in {state.name}
            </h2>
            <p className="text-ink-light text-sm leading-relaxed mb-4 font-ui">
              Paradise Borrowed connects you with neighbors across {state.name} who are willing to lend their items for a fraction of the retail price. Whether you need a power drill for a weekend project, baby gear for visiting grandchildren, or camping equipment for a trip — find it all from locals near you.
            </p>
            <p className="text-ink-light text-sm leading-relaxed font-ui">
              Browse items in {actualStateSlug === 'washington-dc' ? 'neighborhoods' : 'cities'} like {state.cities.slice(0, 5).map(c => c.name).join(", ")}, and more. Save money, reduce waste, and build community through peer-to-peer borrowing.
            </p>
          </div>
        </div>
      </section>

      {/* Ad Banner - Pre-CTA */}
      <div className="py-6 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AdBanner size="responsive" position="pre-cta" />
        </div>
      </div>

      {/* CTA */}
      <section className="py-16 bg-teal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl sm:text-3xl text-white mb-3 tracking-tight">
            Have Items to Lend in {state.name}?
          </h2>
          <p className="text-white/80 text-sm max-w-md mx-auto mb-6 font-ui">
            Turn your idle items into income. List your gear and start earning from neighbors.
          </p>
          <Link to="/create-listing">
            <Button size="lg" className="rounded-full px-8 bg-white hover:bg-sand text-teal font-medium btn-press font-ui" data-testid="state-cta-lend">
              Start Lending
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
